import { Button, DatePicker, Empty, Radio, Select } from '@maxtropy/components';
import { Space, Spin } from 'antd';
import styles from './index.module.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateType, DateTypeDisplay, EnergyMediumIconDisplay, EnergyMediumSceneIds } from '../../utils';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost,
  apiV2ProductionCostGetProductionCostPost,
  apiV2ProductionCostGetProductionListPost,
} from '@maxtropy/device-customer-apis-v2';
import { GlicoUnitComsumptionContext } from '../..';
import { isNil } from 'lodash-es';
import dayjs, { Dayjs } from 'dayjs';
import { RadioChangeEvent } from 'antd/lib/radio';
import { useNavigate } from 'react-router-dom';
import CardContent from './components/CardContent';
import Chart from './components/Chart';
import ReCalcModal from './components/ReCalcModal';
import qs from 'qs';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';

const { RangePicker } = DatePicker;

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const RightContent = () => {
  const hasRecalc = useHasPermission(PermissionsType.B_RECALCULATION); // 重计算
  const hasConfig = useHasPermission(PermissionsType.B_SINGLECONSUMPTIONCONFIG); // 配置
  const { unitId } = React.useContext(GlicoUnitComsumptionContext);
  const navigate = useNavigate();
  // 顶部选择综合能耗还是能源介质, 综合能耗的 id 为 1, 其他能源介质为其 id
  const [selectedConsumption, setSelectedConsumption] = useState<number | undefined>();
  const [dates, setDates] = useState<RangeValue>(null);
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.MIN);
  // 日期选择器默认选择当天
  const [value, setValue] = useState<RangeValue>([dayjs(), dayjs()]);
  const [displayValue, setDisplayValue] = useState<Dayjs>(dayjs());
  const [rangeTime, setRangeTime] = useState<RangeValue>();
  const timer = useRef<any>();
  const [productionId, setProductionId] = useState<number>();
  const [openModal, setOpenModal] = useState<boolean>(false); // 用能重计算弹窗

  // 获取展示产品列表（按日按月）
  const { data: productionList } = useRequest(
    () =>
      apiV2ProductionCostGetProductionListPost({
        energyUnitId: String(unitId),
        startTime: rangeTime?.[0]?.valueOf(),
        endTime: rangeTime?.[1]?.valueOf(),
      }).then(res => {
        return res.list;
      }),
    {
      ready: timeResolution !== DateType.MIN && !!unitId && !!rangeTime,
      refreshDeps: [unitId, rangeTime],
    }
  );

  // 默认选择第一个
  useEffect(() => {
    if (!productionList || productionList.length === 0) return;
    setProductionId(productionList?.[0].id);
  }, [productionList]);

  // 获取能源介质
  const { data: energyMediumList } = useRequest(
    () =>
      apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost({
        energyUnitId: String(unitId),
      }).then(res => {
        return res.list;
      }),

    {
      ready: !!unitId,
      refreshDeps: [unitId],
    }
  );

  // tabs format
  const energyMediumListTabs = useMemo(() => {
    return (energyMediumList ?? []).map(item => ({
      name: item.energyName,
      id: item.id,
      sceneId: item.sceneId,
    }));
  }, [energyMediumList]);

  // 默认选择综合能耗
  useEffect(() => {
    if (!energyMediumList || energyMediumList.length === 0) return;
    setSelectedConsumption(energyMediumList[0].id);
  }, [energyMediumList]);

  // 切换groupId重置mediumId
  // useEffect(() => {
  //   console.log('groupId', groupId);
  //   if (!energyMediumList || energyMediumList.length === 0) return;
  //   setSelectedConsumption(undefined);
  //   if (groupId) {
  //     timer.current = setTimeout(() => {
  //       setSelectedConsumption(energyMediumList?.[0].id);
  //     }, 0);
  //   }
  //   return () => clearTimeout(timer.current);
  // }, [groupId, energyMediumList]);

  // 获取单耗信息
  const { data, loading } = useRequest(
    () => {
      let params = {
        energyMediumId: selectedConsumption,
        energyUnitId: unitId,
        startTime: rangeTime?.[0]?.valueOf(),
        endTime: rangeTime?.[1]?.valueOf(),
        timeResolution: timeResolution,
      };
      if (timeResolution !== DateType.MIN) {
        if (productionId) {
          return apiV2ProductionCostGetProductionCostPost({
            ...params,
            showProductionId: productionId,
          });
        } else {
          return Promise.resolve(undefined);
        }
      } else {
        return apiV2ProductionCostGetProductionCostPost(params);
      }
    },
    {
      ready: !!unitId && !!rangeTime && !!selectedConsumption,
      refreshDeps: [selectedConsumption, unitId, rangeTime, productionId],
    }
  );

  // format startDate endDate
  useEffect(() => {
    if (timeResolution === DateType.DAY) {
      setRangeTime([value?.[0]?.startOf('D') ?? null, value?.[1]?.endOf('D') ?? null]);
    } else if (timeResolution === DateType.MONTH) {
      setRangeTime([value?.[0]?.startOf('month') ?? null, value?.[1]?.endOf('month') ?? null]);
    } else {
      if (!displayValue) return;
      const startTime = displayValue.startOf('day');
      const endTime = displayValue.endOf('day');
      setRangeTime([startTime, endTime]);
    }
  }, [value, timeResolution, displayValue]);

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  // 时间跨度为31天
  const customDisabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'days') >= 31;
    const tooEarly = current && current > dayjs().endOf('day');
    return !!tooEarly || !!tooLate;
  };

  // 时间跨度12个月
  const customDisabledMonthDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 12;
    const tooEarly = current && current > dayjs().endOf('month');
    return !!tooEarly || !!tooLate;
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    setTimeResolution(value);
    if (value === DateType.MIN) {
      setDisplayValue(dayjs());
    } else if (value === DateType.DAY) {
      setValue([dayjs().subtract(31, 'day'), dayjs()]);
    } else {
      setValue([dayjs().subtract(1, 'year'), dayjs()]);
    }
  };

  const exportAction = () => {
    const params = {
      energyMediumId: selectedConsumption,
      energyUnitId: unitId,
      showProductionId: timeResolution === DateType.MIN ? undefined : productionId,
      startTime: rangeTime?.[0]?.valueOf(),
      endTime: rangeTime?.[1]?.valueOf(),
      timeResolution: timeResolution,
    };
    window.open(`/api/v2/production/cost/export` + qs.stringify(params, { addQueryPrefix: true }));
  };

  return (
    <>
      {!unitId ? (
        <>
          {hasConfig && (
            <div className={styles.config_btn}>
              <Button type="primary" onClick={() => navigate(`/glico/unit/consumption/productionConfig`)}>
                配置
              </Button>
            </div>
          )}
          <Empty className={styles.empty} />
        </>
      ) : (
        <Spin spinning={loading}>
          <Space className={styles.tab_box} size="small" wrap>
            {energyMediumListTabs.map(medium => (
              <Button
                key={medium.id}
                style={{ paddingLeft: 12, paddingRight: 16 }}
                onClick={() => {
                  setSelectedConsumption(medium.id);
                }}
                className={selectedConsumption === medium.id ? styles.titleButtonChecked : styles.titleButtonUnChecked}
              >
                <Space size="small" align="center">
                  <img
                    width={24}
                    src={
                      selectedConsumption === medium.id
                        ? EnergyMediumIconDisplay[medium.sceneId as EnergyMediumSceneIds].active
                        : EnergyMediumIconDisplay[medium.sceneId as EnergyMediumSceneIds].passive
                    }
                    // src={generalActive}
                    alt=""
                  />
                  <span>{medium.name}</span>
                </Space>
              </Button>
            ))}
          </Space>
          <div className={styles.time_and_btn}>
            {/* 日期选择器 */}
            <Space size={8} style={{ padding: '0px 20px' }}>
              <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
                <Radio.Button value={DateType.MIN}>{DateTypeDisplay[DateType.MIN]}</Radio.Button>
                <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
                <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
              </Radio.Group>
              {timeResolution === DateType.MIN && (
                <DatePicker
                  style={{ width: '250px' }}
                  value={displayValue}
                  disabledDate={disabledDate}
                  onChange={setDisplayValue}
                  allowClear={false}
                />
              )}
              {(timeResolution === DateType.DAY || timeResolution === DateType.MONTH) && (
                <RangePicker
                  style={{ width: '250px' }}
                  picker={timeResolution === DateType.DAY ? 'date' : 'month'}
                  value={dates || value}
                  disabledDate={timeResolution === DateType.DAY ? customDisabledDate : customDisabledMonthDate}
                  onCalendarChange={val => {
                    setDates(val);
                  }}
                  onChange={val => {
                    setValue(val);
                  }}
                  onOpenChange={onOpenChange}
                  allowClear={false}
                />
              )}
            </Space>
            {/* 按钮 */}
            <Space size={8} style={{ padding: '0px 20px' }}>
              {hasRecalc && (
                <Button type="primary" onClick={() => setOpenModal(true)}>
                  重计算
                </Button>
              )}
              {hasConfig && (
                <Button type="primary" onClick={() => navigate(`/glico/unit/consumption/productionConfig`)}>
                  配置
                </Button>
              )}
              <Button type="primary" onClick={() => exportAction()}>
                导出
              </Button>
            </Space>
          </div>
          <div className={styles.card}>
            <CardContent data={data} />
          </div>
          {timeResolution !== DateType.MIN && (
            <div className={styles.filterArea}>
              <div className={styles.option}>展示产品：</div>
              <Select
                placeholder="请选择"
                className={styles.selectBox}
                value={productionId}
                allowClear={false}
                options={(productionList ?? []).map(item => ({
                  label: item.name,
                  value: item.id,
                }))}
                onChange={val => {
                  setProductionId(val);
                }}
              />
            </div>
          )}
          <div className={styles.chart_box}>
            <Chart data={data} loading={loading} timeResolution={timeResolution} />
          </div>
        </Spin>
      )}
      {/* 重计算 */}
      <ReCalcModal
        openModal={openModal}
        onCancel={() => {
          setOpenModal(false);
        }}
      />
    </>
  );
};

export default RightContent;
